import { theme } from "config/theme";
import React from "react";

export class RGB {
    public readonly r: number;
    public readonly g: number;
    public readonly b: number;

    constructor(r: number, g: number, b: number) {
        this.r = r;
        this.g = g;
        this.b = b;
    }

    public static fromString(color: string): RGB {
        color = color.trim();

        if (color.startsWith('#')) {
            let hex = color.slice(1);
            if (hex.length === 3) {
                hex = hex
                    .split('')
                    .map((c) => c + c)
                    .join('');
            }
            if (hex.length !== 6) return new RGB(0, 0, 0);

            const r = parseInt(hex.substring(0, 2), 16);
            const g = parseInt(hex.substring(2, 4), 16);
            const b = parseInt(hex.substring(4, 6), 16);
            return new RGB(r, g, b);
        }

        if (color.startsWith('rgb')) {
            const match = color.match(/rgba?\(([^)]+)\)/);
            if (match) {
                const [r, g, b] = match[1]
                    .split(',')
                    .slice(0, 3)
                    .map((x) => parseFloat(x.trim()));
                return new RGB(r, g, b);
            }
        }

        return new RGB(255, 255, 255);
    }

    /**
     * Determines whether black or white text provides better contrast.
     */
    public static getContrastTextColor(color: string): string {
        const rgb = RGB.fromString(color);
        const L = rgb.getRelativeLuminance();

        // Contrast ratio formula: (L1 + 0.05) / (L2 + 0.05)
        const contrastWithWhite = 1.05 / (L + 0.05);
        const contrastWithBlack = (L + 0.05) / 0.05;

        return contrastWithWhite > contrastWithBlack
            ? theme.palette.common.white
            : theme.palette.common.black;
    }

    /** Helper: returns style object with background + contrast text */
    public static getStyleForBackground(color: string): React.CSSProperties {
        return {
            backgroundColor: color,
            color: RGB.getContrastTextColor(color),
        };
    }

    /**
     * Returns the relative luminance (WCAG 2.0)
     * Formula: https://www.w3.org/TR/WCAG20/#relativeluminancedef
     */
    public getRelativeLuminance(): number {
        const transform = (value: number) => {
            const c = value / 255;
            return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
        };

        const R = transform(this.r);
        const G = transform(this.g);
        const B = transform(this.b);

        return 0.2126 * R + 0.7152 * G + 0.0722 * B;
    }
}
